import { useEffect, useState } from 'react';
import { get } from 'lodash';
import { DataSourceItem } from '@lendi-ui/auto-complete';

import { searchIndustries } from '@funnels/api';
import { FIELD_PATHS } from '@funnels/constants';
import { Industry } from '@funnels/types';

import { useFormValues } from '../useFormValues';

type MappedIndustry = {
  label: string;
  value: string;
};

export function mapIndustries(industries: Industry[]) {
  return industries.map((industry) => ({
    label: `${industry.code} - ${industry.name}`,
    value: industry.code,
  }));
}

export function filterIndustries(industries: Industry[], query: string) {
  if (!query) return industries;

  return industries.filter((industry) => {
    const foundInDescription = industry.description.some((desc) =>
      desc.toLowerCase().includes(query.toLocaleLowerCase())
    );
    return (
      foundInDescription || industry.name.toLowerCase().includes(query.toLowerCase()) || industry.code.includes(query)
    );
  });
}

type UseIndustries = [MappedIndustry[], string, string, (v: string) => void, () => void, (v: DataSourceItem) => void];

export function useIndustries(applicantIndex: number): UseIndustries {
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [formValues, setFormValue] = useFormValues();
  const industryCode = get(formValues, FIELD_PATHS.APPLICANT_TRUST_INDUSTRY_CODE(applicantIndex), '');
  const industryName = industries.find((industry) => industry.code === industryCode)?.name;
  const [searchQuery, setSearchQuery] = useState(industryName || '');

  const filteredIndustries = filterIndustries(industries, searchQuery);
  const mappedIndustries = mapIndustries(filteredIndustries);

  const displayValue = industryCode && industryName ? `${industryCode} - ${industryName}` : '';

  function handleChange(newValue: string) {
    setSearchQuery(newValue);
  }

  function handleReset() {
    setSearchQuery('');
    setFormValue(FIELD_PATHS.APPLICANT_TRUST_INDUSTRY_CODE(applicantIndex), undefined);
  }

  function handleSelectedItem(item: DataSourceItem) {
    const industry = industries.find((industry) => industry.code === item.value);

    setSearchQuery(industry?.name ?? '');
    setFormValue(FIELD_PATHS.APPLICANT_TRUST_INDUSTRY_CODE(applicantIndex), industry?.code);
  }

  useEffect(() => {
    searchIndustries().then(setIndustries);
  }, []);

  return [mappedIndustries, displayValue, searchQuery, handleChange, handleReset, handleSelectedItem];
}
